import { Injectable } from '@angular/core';
import { ProjectModule } from '../../resources/module/project-module.interface';

/**
 * Chapter service.
 *
 * Adds the correct chapter number to each submodule, module and element.
 */
@Injectable({
  providedIn: 'root'
})
export class ChaptersService {

  /** Module chapter. */
  private readonly CHAPTER_MODULE_INITIAL = 0;
  private chapterModule = this.CHAPTER_MODULE_INITIAL;

  /** Submodule chapter. */
  private readonly CHAPTER_SUBMODULE_INITIAL = 0;
  private chapterSubmodule = this.CHAPTER_SUBMODULE_INITIAL;

  /** Element chapter. */
  private readonly CHAPTER_ELEMENT_INITIAL = 1;
  private chapterElement = this.CHAPTER_ELEMENT_INITIAL;


  /**
   * Update the complete project with chapter numbers.
   *
   * This function should run on every update to the project.
   *
   * @todo The `chapterSubmodule` property is (or should be) depricated. Added these lines as a comment for
   *  emergency fallback, in case it's breaking the info page or the pdf exports. Remove when safe/possible.
   *
   * @param modules The project's modules.
   * @returns The project's modules with chapter numbers.
   */
  public chapterize = (modules: ProjectModule[]): ProjectModule[] => {
    this._resetAllChapters();

    // Modules.
    for (const module of modules) {
      module.chapter = null;
      if (!module.hide && module.active && !module.isTechnicalType) {
        this._incrementModuleChapter();
        module.chapter = this._getModuleChapter();
      }

      // Submodules.
      for (const submodule of module.submodules) {
        submodule.chapter = null;
        if (submodule.active && !module.isTechnicalType) {
          if (submodule.name !== 'Algemeen' && module.name !== submodule.name) {
            this._incrementSubmoduleChapter();
            submodule.chapter = this._getSubmoduleChapter();
          }
        }

        // Elements.
        for (const element of submodule.elements) {
          element.chapter = null;
          if (!element.hide && element.active && !module.isTechnicalType) {
            if (submodule.name === 'Algemeen') {
              if (element.name !== 'Algemeen') {
                this._incrementSubmoduleChapter();
                element.chapter = this._getSubmoduleChapter();
              }
            } else {
              if (element.name === 'Algemeen') {

              } else if (element.name === submodule.name && submodule.name === module.name) {

              } else if (element.name === submodule.name && submodule.name !== module.name) {

              } else if (element.name !== submodule.name && submodule.name !== module.name) {
                element.chapter = this._getElementChapter();
                this._incrementElementChapter();
              } else {
                this._incrementSubmoduleChapter();
                element.chapter = this._getSubmoduleChapter();
              }
            }
          }
        }

        // For each new submodule, reset element chapter to initial state.
        this._resetElementChapter();
      }

      // For each new module, reset element chapter to initial state.
      this._resetSubmoduleChapter();
    }

    return modules;
  }

  /**
   * Get module chapter number.
   *
   * @returns Module chapter number.
   */
  private _getModuleChapter(): string {
    return `${ this.chapterModule }`;
  }

  /**
   * Get submodule chapter number.
   *
   * @returns Submodule chapter number.
   */
  private _getSubmoduleChapter(): string {
    return `${ this.chapterModule }.${ this.chapterSubmodule }`;
  }

  /**
   * Get element chapter number.
   *
   * @returns Element chapter number.
   */
  private _getElementChapter(): string {
    return `${ this.chapterModule }.${ this.chapterSubmodule }.${ this.chapterElement }`;
  }

  /**
   * Increment module chapter number by 1.
   */
  private _incrementModuleChapter(): void {
    this.chapterModule++;
  }

  /**
   * Increment submodule chapter number by 1.
   */
  private _incrementSubmoduleChapter(): void {
    this.chapterSubmodule++;
  }

  /**
   * Increment element chapter number by 1.
   */
  private _incrementElementChapter(): void {
    this.chapterElement++;
  }

  /**
   * Reset all chapters to initial state.
   */
  private _resetAllChapters(): void {
    this._resetModuleChapter();
    this._resetSubmoduleChapter();
    this._resetElementChapter();
  }

  /**
   * Reset module chapter number.
   */
  private _resetModuleChapter(): void {
    this.chapterModule = this.CHAPTER_MODULE_INITIAL;
  }

  /**
   * Reset submodule chapter number.
   */
  private _resetSubmoduleChapter(): void {
    this.chapterSubmodule = this.CHAPTER_SUBMODULE_INITIAL;
  }

  /**
   * Reset element chapter number.
   */
  private _resetElementChapter(): void {
    this.chapterElement = this.CHAPTER_ELEMENT_INITIAL;
  }
}
